import React, { useState } from 'react';

const JobsCareers = () => {
  const [selectedJob, setSelectedJob] = useState(null);

  const jobOpenings = [
    {
      id: 1,
      title: "Senior Full Stack Developer",
      department: "Engineering",
      location: "Lagos, Nigeria",
      type: "Full-time",
      experience: "3-5 years",
      description: "We're looking for a passionate full-stack developer to join our engineering team and help build the future of e-commerce.",
      requirements: [
        "3+ years of experience with React and Node.js",
        "Experience with cloud platforms (AWS, Azure, or GCP)",
        "Strong understanding of database design and optimization",
        "Experience with microservices architecture",
        "Excellent problem-solving and communication skills"
      ],
      responsibilities: [
        "Develop and maintain our core platform features",
        "Collaborate with cross-functional teams to deliver high-quality software",
        "Participate in code reviews and technical discussions",
        "Mentor junior developers and contribute to team growth"
      ]
    },
    {
      id: 2,
      title: "Business Development Manager",
      department: "Business Development",
      location: "Lagos, Nigeria",
      type: "Full-time",
      experience: "2-4 years",
      description: "Join our business development team to help expand our network of suppliers and partners in China and Nigeria.",
      requirements: [
        "2+ years of experience in business development or sales",
        "Understanding of international trade and e-commerce",
        "Strong networking and relationship-building skills",
        "Experience working with Chinese or international partners",
        "Fluent in English (Mandarin is a plus)"
      ],
      responsibilities: [
        "Identify and onboard new suppliers and partners",
        "Build and maintain relationships with key stakeholders",
        "Develop strategies to expand our market presence",
        "Collaborate with operations team to ensure smooth partnerships"
      ]
    },
    {
      id: 3,
      title: "Customer Success Specialist",
      department: "Customer Success",
      location: "Remote",
      type: "Full-time",
      experience: "1-3 years",
      description: "Help our customers succeed by providing exceptional support and guidance throughout their journey with Checkstall.",
      requirements: [
        "1+ years of customer service or support experience",
        "Excellent communication and problem-solving skills",
        "Experience with CRM systems and support tools",
        "Ability to work independently and manage multiple priorities",
        "Passion for helping customers succeed"
      ],
      responsibilities: [
        "Provide timely and helpful support to our customers",
        "Onboard new customers and guide them through our platform",
        "Collect and analyze customer feedback to improve our services",
        "Collaborate with product team to enhance user experience"
      ]
    },
    {
      id: 4,
      title: "Marketing Manager",
      department: "Marketing",
      location: "Lagos, Nigeria",
      type: "Full-time",
      experience: "3-5 years",
      description: "Lead our marketing efforts to grow our brand awareness and customer base across Nigeria and beyond.",
      requirements: [
        "3+ years of marketing experience, preferably in e-commerce or B2B",
        "Strong digital marketing skills (SEO, SEM, social media)",
        "Experience with content marketing and brand building",
        "Analytical mindset with experience in marketing metrics",
        "Creative thinking and strong writing skills"
      ],
      responsibilities: [
        "Develop and execute comprehensive marketing strategies",
        "Manage digital marketing campaigns across multiple channels",
        "Create compelling content that resonates with our target audience",
        "Analyze marketing performance and optimize for better results"
      ]
    }
  ];

  const companyCulture = [
    {
      title: "Innovation First",
      description: "We encourage creative thinking and embrace new ideas that can improve our platform and services.",
      icon: "💡"
    },
    {
      title: "Customer-Centric",
      description: "Everything we do is focused on providing the best possible experience for our customers.",
      icon: "❤️"
    },
    {
      title: "Collaborative",
      description: "We believe in the power of teamwork and open communication across all departments.",
      icon: "🤝"
    },
    {
      title: "Growth-Oriented",
      description: "We invest in our team's professional development and provide opportunities for career advancement.",
      icon: "📈"
    }
  ];

  const benefits = [
    "Competitive salary and equity options",
    "Health insurance and wellness programs",
    "Flexible working hours and remote work options",
    "Professional development budget",
    "Team building activities and company retreats",
    "Modern office space with all amenities",
    "Opportunity to work with cutting-edge technology",
    "Make a real impact on international trade"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Jobs & <span className="text-primary">Careers</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Join our mission to transform international trade and help Nigerian entrepreneurs access global markets
            </p>
          </div>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Culture</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're building more than just a company - we're creating a community of passionate individuals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyCulture.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-soft p-6 text-center">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Work With Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer more than just a job - we provide an opportunity to make a real difference
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              {benefits.slice(0, 4).map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {benefits.slice(4).map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Current Openings</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our current job opportunities and find your perfect role
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {jobOpenings.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl shadow-soft p-6 hover:shadow-soft-lg transition-shadow duration-300">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                  <span className="bg-primary-100 text-primary px-3 py-1 rounded-full text-sm font-medium">
                    {job.type}
                  </span>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {job.department}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {job.location}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {job.experience}
                  </div>
                </div>

                <p className="text-gray-600 mb-4">{job.description}</p>

                <button
                  onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                  className="w-full bg-primary text-white py-2 px-4 rounded-2xl font-medium hover:bg-primary-600 transition-colors duration-200"
                >
                  {selectedJob === job.id ? 'Hide Details' : 'View Details'}
                </button>

                {selectedJob === job.id && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h4>
                      <ul className="space-y-2">
                        {job.requirements.map((req, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-600 text-sm">{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Responsibilities</h4>
                      <ul className="space-y-2">
                        {job.responsibilities.map((resp, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-600 text-sm">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button className="flex-1 bg-primary text-white py-2 px-4 rounded-2xl font-medium hover:bg-primary-600 transition-colors duration-200">
                        Apply Now
                      </button>
                      <button className="flex-1 border border-primary text-primary py-2 px-4 rounded-2xl font-medium hover:bg-primary hover:text-white transition-colors duration-200">
                        Share Job
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Don't See Your Role?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
            We're always looking for talented individuals who share our vision. Send us your resume and let's start a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-primary px-8 py-3 rounded-2xl font-semibold hover:bg-gray-50 transition-colors duration-200"
            >
              Send Your Resume
            </a>
            <a
              href="mailto:careers@checkstall.com"
              className="border-2 border-white text-white px-8 py-3 rounded-2xl font-semibold hover:bg-white hover:text-primary transition-colors duration-200"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobsCareers;


